import React, { ReactNode, useState } from "react";
import Confirm from "./Confirm";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";

export function DeleteConfirm<T>({
    item,
    title,
    onHide,
    onDelete,
    fullTitle,
    text,
    confirmButtonText,
    children,
}: {
    item: T | undefined | null;
    title: string | undefined | null;
    onHide: () => void;
    onDelete: (item: T) => void | Promise<void>;
    fullTitle?: string;
    text?: string;
    confirmButtonText?: string;
    children?: ReactNode;
}) {
    return (
        <Confirm
            title={fullTitle || `Delete ${title}`}
            shouldShow={item != null}
            confirmText={confirmButtonText as string}
            closeText="Cancel"
            closeVariant="outline-gray"
            confirmVariant="danger"
            dialogClassName="centered-container-md"
            onHide={() => onHide()}
            onConfirm={async () => {
                LoggingHandler.instance.wrap(async () => {
                    await onDelete(item as T);
                    onHide();
                });
            }}
        >
            {children || (
                <>
                    {text || "Are you sure you want to delete"}
                    {" "}
                    {title ? `"${title}"` : "this item"}
                    ?
                </>
            )}
        </Confirm>
    );
}

DeleteConfirm.defaultProps = {
    fullTitle: undefined,
    text: undefined,
    confirmButtonText: "Delete",
    children: undefined,
};

export default function useDeleteConfirm<T>(
    onDelete: (item: T) => void | Promise<void>,
    onExtractTitle: ((item: T) => string | null | undefined),
    title?: string,
    text?: string,
    confirmButtonText?: string,
) {
    const [deleting, setDeleting] = useState<T>();

    return {
        setDeleting,
        element: (
            <DeleteConfirm
                title={(deleting != null ? onExtractTitle(deleting) : undefined)}
                item={deleting}
                onHide={() => setDeleting(undefined)}
                onDelete={item => onDelete(item)}
                fullTitle={title}
                text={text}
                confirmButtonText={confirmButtonText}
            />
        ),
    };
}

export function useDeleteConfirmWithBody<T>(
    onDelete: (item: T) => void | Promise<void>,
    title?: string,
    children?: ReactNode,
    confirmButtonText?: string,
) {
    const [deleting, setDeleting] = useState<T>();

    return {
        setDeleting,
        element: (
            <DeleteConfirm
                title=""
                item={deleting}
                onHide={() => setDeleting(undefined)}
                onDelete={item => onDelete(item)}
                fullTitle={title}
                confirmButtonText={confirmButtonText}
            >
                {children}
            </DeleteConfirm>
        ),
    };
}
